/**
 * Wizard-Step-Synchronisation für das TravelMenu.
 * Reine Funktionen die DOM + State synchronisieren.
 */
import type { TravelMenuDOMResult } from './TravelMenuDOM';
import { setModeCardActive } from './TravelMenuDOM';
import type { TravelMenuState } from './TravelMenuState';
import { SHIP_NAMES } from './TravelMenuState';
import { SHIP_TYPE_DEFS } from '../config/ShipTypes';

/** UI-Card-Index → Server travel_mode. Normal=0, Warp=2, Reveal=1. */
export const CARD_TO_TRAVEL_MODE = [0, 2, 1, -1] as const;

export function syncStep1(dom: TravelMenuDOMResult, state: TravelMenuState): void {
  const fromHex = !!state.stationedSourceId;
  const startSection = dom.startSelect.parentElement!;

  if (fromHex && state.selectedStart) {
    startSection.style.display = 'none';
    dom.startLabel.textContent = `\u{1F4CD} Start: ${state.selectedStart.label}`;
    dom.startLabel.style.display = 'block';
    Object.assign(dom.startLabel.style, {
      color: '#d4a020', fontWeight: '700', fontSize: '12px',
      padding: '6px 0', marginBottom: '4px',
    });
  } else {
    startSection.style.display = '';
    if (state.selectedStart?.planetId) dom.startSelect.value = state.selectedStart.planetId;

    if (state.selectedStart && !state.selectedStart.planetId) {
      dom.startLabel.textContent = state.selectedStart.label;
      dom.startLabel.style.display = 'block';
      Object.assign(dom.startLabel.style, {
        color: '#8cf', fontWeight: '400', fontSize: '', padding: '', marginBottom: '',
      });
    } else {
      dom.startLabel.style.display = 'none';
    }
  }

  if (state.selectedEnd?.planetId) dom.endSelect.value = state.selectedEnd.planetId;

  if (state.selectedEnd && !state.selectedEnd.planetId) {
    dom.endLabel.textContent = state.selectedEnd.label;
    dom.endLabel.style.display = 'block';
  } else {
    dom.endLabel.style.display = 'none';
  }
}

export function syncSliderLabel(dom: TravelMenuDOMResult, state: TravelMenuState, i: number): void {
  dom.sliderLabels[i].textContent = `${state.shipSliders[i]}/${state.maxShips[i]}`;
}

export function syncSliders(dom: TravelMenuDOMResult, state: TravelMenuState): void {
  const max = state.maxShips;
  for (let i = 0; i < 8; i++) {
    dom.sliders[i].max = String(max[i]);
    dom.sliders[i].value = String(state.shipSliders[i]);
    syncSliderLabel(dom, state, i);
  }
}

export function syncWarpInfo(dom: TravelMenuDOMResult, state: TravelMenuState): void {
  const el = dom.warpInfoEl;
  const total = state.totalSelectedShips;

  if (total === 0) { el.style.display = 'none'; return; }

  el.style.display = 'flex';
  el.innerHTML = '';
  const info = state.getWarpInfo();

  const hasCarrier = state.shipSliders.some((c, i) => c > 0 && SHIP_TYPE_DEFS[i].warpCarry);
  if (hasCarrier) {
    const capTitle = document.createElement('span');
    capTitle.textContent = 'Carrier-Kapazit\u00E4t:';
    Object.assign(capTitle.style, { color: '#5a7a9a', fontSize: '10px', marginBottom: '2px' });
    el.appendChild(capTitle);

    const capRow = document.createElement('div');
    Object.assign(capRow.style, { display: 'flex', flexWrap: 'wrap', gap: '6px', fontSize: '10px' });
    for (let s = 0; s < 8; s++) {
      if (info.carrierCap[s] <= 0 && state.shipSliders[s] <= 0) continue;
      if (SHIP_TYPE_DEFS[s].warpCapable) continue;
      const count = state.shipSliders[s];
      const cap = info.carrierCap[s];
      const span = document.createElement('span');
      span.textContent = `${SHIP_NAMES[s]} ${count}/${cap}`;
      span.style.color = count > cap ? '#f66' : '#7a9a7a';
      capRow.appendChild(span);
    }
    el.appendChild(capRow);
  }

  const statusLine = document.createElement('span');
  if (info.canWarp) {
    statusLine.textContent = '\u26A1 Warp m\u00F6glich';
    statusLine.style.color = '#8f8';
  } else {
    statusLine.textContent = `\u26A0 ${info.reason}`;
    statusLine.style.color = '#fa0';
  }
  Object.assign(statusLine.style, { fontSize: '11px', fontWeight: '700', marginTop: '2px' });
  el.appendChild(statusLine);
}

export function syncModeCards(dom: TravelMenuDOMResult, state: TravelMenuState): void {
  const warpInfo = state.getWarpInfo();

  for (let i = 0; i < dom.modeCards.length; i++) {
    const card = dom.modeCards[i];
    const travelMode = CARD_TO_TRAVEL_MODE[i];
    const isSelected = travelMode === state.selectedMode;

    if (i === 1) {
      if (!warpInfo.canWarp) {
        card.dataset.enabled = '0';
        card.style.opacity = '0.4';
        card.style.cursor = 'default';
        const desc = card.querySelector('.mode-desc') as HTMLSpanElement;
        if (desc) desc.textContent = warpInfo.reason;
        if (state.selectedMode === 2) state.selectedMode = 0;
      } else {
        card.dataset.enabled = '1';
        card.style.opacity = '1';
        card.style.cursor = 'pointer';
        const desc = card.querySelector('.mode-desc') as HTMLSpanElement;
        if (desc) desc.textContent = 'Schnelle Reise';
      }
    }
    setModeCardActive(card, isSelected && card.dataset.enabled === '1');
  }
}

export function syncSendBtn(dom: TravelMenuDOMResult, state: TravelMenuState): void {
  const can = state.canSend;
  Object.assign(dom.sendBtn.style, {
    opacity: can ? '1' : '0.35',
    pointerEvents: can ? 'auto' : 'none',
  });
}
